import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AttachmentApiService } from '../../data-access/attachment-api.service';
import { FileTypeEnum, DocumentTypeEnum } from '../../models';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';

interface DocumentTypeOption {
  label: string;
  value: DocumentTypeEnum;
}

@Component({
  selector: 'lib-mobile-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './mobile-upload.component.html',
  styleUrls: ['./mobile-upload.component.css']
})
export class MobileUploadComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly attachmentApiService = inject(AttachmentApiService);
  private readonly translateService = inject(TranslateService);

  uploadType: 'patient' | 'work-order' = 'patient';
  targetId = '';
  token = '';

  // Info fields
  targetName = signal<string>('');
  patientNo = signal<string>('');
  workOrderNo = signal<string>('');
  patientNameForWorkOrder = signal<string>('');
  isLoadingInfo = signal<boolean>(true);
  errorLoadingInfo = signal<string | null>(null);

  // Upload fields
  documentTypes = signal<DocumentTypeOption[]>([]);
  selectedDocType = signal<DocumentTypeEnum | null>(null);
  selectedFile = signal<File | null>(null);
  isUploading = signal<boolean>(false);
  uploadProgress = signal<number>(0);
  uploadedFilesCount = signal<number>(0);
  uploadSuccess = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    // Set language from query parameter
    const lang = this.route.snapshot.queryParamMap.get('lang') || 'tr';
    this.translateService.setLanguage(lang);
    Promise.all([
      this.translateService.loadLibrary('attachment').catch((err: unknown) => console.warn(err)),
      this.translateService.loadLibrary('patient').catch((err: unknown) => console.warn(err)),
      this.translateService.loadLibrary('workorder').catch((err: unknown) => console.warn(err))
    ]).finally(() => {
      this.initializeApp();
    });
  }

  private initializeApp(): void {
    // Determine target entity type and IDs
    const patientIdParam = this.route.snapshot.paramMap.get('patientId');
    const workOrderIdParam = this.route.snapshot.paramMap.get('workOrderId');
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    const workOrderNoParam = this.route.snapshot.queryParamMap.get('workOrderNo') || '';
    const patientNoParam = this.route.snapshot.queryParamMap.get('patientNo') || '';

    if (patientIdParam) {
      this.uploadType = 'patient';
      this.targetId = patientIdParam;
      this.patientNo.set(patientNoParam);
      this.targetName.set(patientNoParam || patientIdParam);
      this.documentTypes.set([
        { label: 'PATIENT.DOCUMENT_TYPE_SCANNER_3D', value: DocumentTypeEnum.Scanner3D },
        { label: 'PATIENT.DOCUMENT_TYPE_X_RAY', value: DocumentTypeEnum.XRay },
        { label: 'PATIENT.DOCUMENT_TYPE_CBCT', value: DocumentTypeEnum.CBCT },
        { label: 'PATIENT.DOCUMENT_TYPE_PICTURE_IN_MOUTH', value: DocumentTypeEnum.PictureInMounth },
        { label: 'PATIENT.DOCUMENT_TYPE_PICTURE_IN_FACE', value: DocumentTypeEnum.PictureInFace },
        { label: 'PATIENT.DOCUMENT_TYPE_TREATMENT_PLAN', value: DocumentTypeEnum.TreatmentPlan },
        { label: 'PATIENT.DOCUMENT_TYPE_LAB_REPORT', value: DocumentTypeEnum.LabReport },
        { label: 'PATIENT.DOCUMENT_TYPE_OTHER', value: DocumentTypeEnum.Other }
      ]);
      this.selectedDocType.set(DocumentTypeEnum.Scanner3D);
    } else if (workOrderIdParam) {
      this.uploadType = 'work-order';
      this.targetId = workOrderIdParam;
      this.workOrderNo.set(workOrderNoParam || workOrderIdParam);
      this.targetName.set(`WO-${workOrderNoParam || workOrderIdParam}`);
      this.documentTypes.set([
        { label: 'PATIENT.DOCUMENT_TYPE_SCANNER_3D', value: DocumentTypeEnum.Scanner3D },
        { label: 'PATIENT.DOCUMENT_TYPE_X_RAY', value: DocumentTypeEnum.XRay },
        { label: 'PATIENT.DOCUMENT_TYPE_CBCT', value: DocumentTypeEnum.CBCT },
        { label: 'PATIENT.DOCUMENT_TYPE_PICTURE_IN_MOUTH', value: DocumentTypeEnum.PictureInMounth },
        { label: 'PATIENT.DOCUMENT_TYPE_PICTURE_IN_FACE', value: DocumentTypeEnum.PictureInFace },
        { label: 'PATIENT.DOCUMENT_TYPE_TREATMENT_PLAN', value: DocumentTypeEnum.TreatmentPlan },
        { label: 'PATIENT.DOCUMENT_TYPE_LAB_REPORT', value: DocumentTypeEnum.LabReport },
        { label: 'PATIENT.DOCUMENT_TYPE_OTHER', value: DocumentTypeEnum.Other }
      ]);
      this.selectedDocType.set(DocumentTypeEnum.PictureInFace);
    } else {
      this.errorLoadingInfo.set(this.translateService.getText('ATTACHMENT.INVALID_ADDRESS'));
      this.isLoadingInfo.set(false);
      return;
    }

    if (!this.token) {
      this.errorLoadingInfo.set(this.translateService.getText('ATTACHMENT.NO_SESSION'));
      this.isLoadingInfo.set(false);
      return;
    }

    this.isLoadingInfo.set(false);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this.selectedFile.set(files[0]);
      this.errorMessage.set(null);
    }
  }

  triggerFileInput(inputElement: HTMLInputElement): void {
    inputElement.click();
  }

  upload(): void {
    const file = this.selectedFile();
    const docType = this.selectedDocType();

    if (!file) {
      this.errorMessage.set(this.translateService.getText('ATTACHMENT.REQUIRED_FILE'));
      return;
    }

    if (docType === null) {
      this.errorMessage.set(this.translateService.getText('ATTACHMENT.REQUIRED_DOC_TYPE'));
      return;
    }

    this.isUploading.set(true);
    this.errorMessage.set(null);
    this.uploadProgress.set(10); // Start progress indicator

    const patientId = this.uploadType === 'patient' ? this.targetId : null;
    const workOrderId = this.uploadType === 'work-order' ? this.targetId : null;
    const fileType = this.uploadType === 'patient' ? FileTypeEnum.Patient : FileTypeEnum.WorkOrder;

    this.attachmentApiService.uploadFileWithQr(
      file,
      patientId,
      workOrderId,
      fileType,
      docType,
      this.token
    ).subscribe({
      next: (res) => {
        this.isUploading.set(false);
        if (res.success) {
          this.uploadedFilesCount.set(this.uploadedFilesCount() + 1);
          this.uploadSuccess.set(true);
          this.selectedFile.set(null);
        } else {
          this.errorMessage.set(res.message || this.translateService.getText('ATTACHMENT.UPLOAD_ERROR'));
        }
      },
      error: (err: unknown) => {
        console.error(err);
        this.isUploading.set(false);
        this.errorMessage.set(this.translateService.getText('ATTACHMENT.UPLOAD_CONN_ERROR'));
      }
    });
  }

  uploadAnother(): void {
    this.uploadSuccess.set(false);
    this.errorMessage.set(null);
    this.selectedFile.set(null);
  }
}
